import { useEffect, useState } from 'react';
import { FiEdit2, FiInfo } from 'react-icons/fi';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { updateSample } from '../../api/data';
import { UpdateSamplePayload } from '../../@types/samples/types';
import { Accordion } from '../accordion';
import { Button } from '../button';
import { TextField } from '../text-field';
import { Select } from '../select';
import styles from './SampleInformation.module.css';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type IconProps = { size?: number; style?: React.CSSProperties };
const PencilIcon = FiEdit2 as React.FC<IconProps>;
const InfoIcon = FiInfo as React.FC<IconProps>;

const DEPTH_UNIT_OPTIONS = [
  { value: 'm',  label: 'm' },
  { value: 'ft', label: 'ft' },
];

const EDITABLE_KEYS = [
  'Sample', 'Well', 'Permeability', 'Porosity', 'Pce', 'Swi', 'Core Sample Length',
];

const COMPUTED_KEYS = ['FZI'];

const REQUIRED_FIELDS = ['Permeability', 'Porosity'];

const FIELD_UNITS: Record<string, string> = {
  Permeability: 'mD',
  Porosity: 'fraction',
  Swi: 'fraction',
};

const FIELD_HINTS: Record<string, string> = {
  Swi: 'If not provided, min(Sw) from curves is used',
};

const SampleInformation = () => {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sampleData = selectedInfo?.sample;

  const [edit, setEdit] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});

  const wellOptions = (data.wells ?? []).map(w => ({ value: w.uid, label: w.well_name }));
  const wellNameByUid = Object.fromEntries((data.wells ?? []).map(w => [w.uid, w.well_name]));

  useEffect(() => {
    if (sampleData) {
      setEditData({
        Sample:               sampleData.sample_code,
        Well:                 sampleData.well_uid,
        Depth:                sampleData.depth,
        Permeability:         sampleData.input_sample_values.permeability,
        Porosity:             sampleData.input_sample_values.porosity,
        FZI:                  sampleData?.input_sample_values?.fzi,
        Swi:                  sampleData.input_sample_values.swi,
        'Core Sample Length': sampleData.sample_length,
        'Depth Unit':         sampleData.depth_unit,
      });
    }
  }, [sampleData]);

  if (!sampleData) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = async () => {
    try {
      setLoadingEdit(true);
      if (edit) {
        const payload: UpdateSamplePayload = {
          sample_uid:   sampleData.uid,
          well_uid:     editData.Well,
          sample_code:  editData.Sample,
          depth_unit:   editData['Depth Unit'],
          sample_length: toNumberOrNull(editData['Core Sample Length']),
          input_sample_values: {
            permeability: toNumberOrNull(editData.Permeability),
            porosity:     toNumberOrNull(editData.Porosity),
            swi:          toNumberOrNull(editData.Swi),
          },
          custom_analyze_values: { pce: toNumberOrNull(editData.Pce) },
        };
        const response = await updateSample(payload);
        updateData({ wellSamples: data.wellSamples.map(s => s.uid === response.uid ? response : s) });
        setSelectedInfo({ sample: response });
      }
    } catch (error) {
      console.error('Edit sample error ===>', error);
    } finally {
      setEdit(e => !e);
      setLoadingEdit(false);
    }
  };

  const header = (
    <>
      <span className={styles.titleRow}>
        Sample Information
        {edit && <span className={styles.editingChip}>Editing</span>}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {edit && (
          <Button variant="secondary" onClick={() => setEdit(false)}>
            Cancel
          </Button>
        )}
        <Button
          variant={edit ? 'primary' : 'secondary'}
          startIcon={!edit ? <PencilIcon size={12} /> : undefined}
          loading={loadingEdit}
          onClick={handleEdit}
        >
          {edit ? 'Save' : 'Edit'}
        </Button>
      </div>
    </>
  );

  /* rows = all keys except 'Depth Unit' (rendered inline with Depth) */
  const rows = Object.entries(editData).filter(([key]) => key !== 'Depth Unit');

  return (
    <Accordion defaultOpen="sample-information">
      <Accordion.Item id="sample-information" header={header} className={edit ? styles.editingRing : undefined}>
        <div className={styles.grid}>
          {rows.map(([key, value]) => {
            const unit = FIELD_UNITS[key];
            const hint = FIELD_HINTS[key];
            const isMissing = value === null || value === undefined || value === '';
            const isRequired = REQUIRED_FIELDS.includes(key);

            return (
              <div key={key} className={styles.row}>
                <span className={styles.cellLabel}>
                  {key}
                  {unit && <span className={styles.cellUnit}> ({unit})</span>}
                </span>
                <div className={styles.cellValue}>
                  {COMPUTED_KEYS.includes(key) ? (
                    <span className={styles.computedValue}>
                      {isMissing ? 'N/A' : String(value)}
                      <span className={styles.computedChip}>Computed</span>
                    </span>
                  ) : edit && EDITABLE_KEYS.includes(key) ? (
                    key === 'Well' ? (
                      <Select
                        size="sm"
                        value={value}
                        options={wellOptions}
                        onChange={val => setEditData(prev => ({ ...prev, [key]: val }))}
                        fullWidth
                      />
                    ) : (
                      <TextField size="sm" name={key} value={value ?? ''} onChange={handleInputChange} fullWidth />
                    )
                  ) : key === 'Well' ? (
                    <span>{wellNameByUid[value] ?? value ?? 'N/A'}</span>
                  ) : key === 'Depth' && edit ? (
                    <div className={styles.depthEdit}>
                      <TextField size="sm" name="Depth" value={value ?? ''} onChange={handleInputChange} fullWidth />
                      <Select
                        size="sm"
                        value={editData['Depth Unit']}
                        options={DEPTH_UNIT_OPTIONS}
                        onChange={val => setEditData(prev => ({ ...prev, 'Depth Unit': val }))}
                      />
                    </div>
                  ) : key === 'Depth' ? (
                    <span>
                      {value}{' '}
                      <span style={{ opacity: 0.6 }}>{editData['Depth Unit']}</span>
                    </span>
                  ) : (
                    <span className={isRequired && isMissing ? styles.cellRequired : undefined}>
                      {isMissing ? (isRequired ? '⚠ Required' : 'N/A') : String(value)}
                    </span>
                  )}
                  {hint && !edit && (
                    <span className={styles.cellHint}>
                      <InfoIcon size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                      {hint}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Accordion.Item>
    </Accordion>
  );
};

export default SampleInformation;
